import { Box, BoxProps, useTheme } from "@mui/material";

interface StyledParentBoxProps extends BoxProps {
  picturePath: string | undefined;
}

export const StyledParentBox: React.FC<StyledParentBoxProps> = ({
  picturePath,
  children,
  sx,
  ...rest
}) => {
  const theme = useTheme();

  const backgroundImage = picturePath
    ? `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}00 120%), url(${picturePath})`
    : "none"; // opacity value was 120%

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "column",
        minWidth: "295px",
        minHeight: "161px",
        borderRadius: "9px",
        fontSize: 18,
        cursor: "pointer",
        overflow: "hidden",
        background: theme.palette.background.paper,
        backgroundImage: backgroundImage,
        backgroundColor: theme.palette.background.paper,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
        "&:hover": {
          boxShadow: "0 0 0 2px rgba(255, 255, 255, 1)"
        },
        "@media (max-device-width: 769px)": {
          fontSize: 14,
          minWidth: "210px",
          minHeight: "145px"
        },
        "@media (max-device-width: 414px)": {
          width: "100%"
        },
        ...sx
      }}
      {...rest}
    >
      {children}
    </Box>
  );
};

/** Opaque footer bar for action icons — matches booking location card footer pattern. */
export const MenuBarCardActionFooter: React.FC<BoxProps> = ({ children, sx, ...rest }) => {
  return (
    <Box sx={{ position: "relative", opacity: 0.85 }} data-testid="edit-item-box" {...rest}>
      <Box
        sx={theme => ({
          position: "absolute",
          inset: 0,
          bgcolor: theme.palette.background.paper,
          opacity: 0.95,
          zIndex: 0,
          borderTop: `0.5px solid ${theme.palette.divider}`,
          ...theme.applyStyles("light", {
            opacity: 1
          })
        })}
      />
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          alignItems: "center",
          px: 0.5,
          ...sx
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export const StyledBodyBox: React.FC<BoxProps> = ({ children, sx, ...rest }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        textAlign: "left",
        fontSize: "inherit",
        maxWidth: "250px",
        cursor: "pointer",
        ...sx
      }}
      {...rest}
    >
      {children}
    </Box>
  );
};

export const MenuBarCardContentBox: React.FC<BoxProps> = ({ children, sx, ...rest }) => {
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        padding: "21px",
        minHeight: 0,
        ...sx
      }}
      {...rest}
    >
      {children}
    </Box>
  );
};
